import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { ArrowDownToLine, ArrowUpFromLine, Banknote, Loader2 } from 'lucide-react';

/**
 * CashMovementDialog - For Cash In, Cash Out, and Deposit operations
 * @param {boolean} open - Whether dialog is open
 * @param {function} onClose - Called when dialog closes
 * @param {function} onConfirm - Called with movement data when confirmed
 * @param {string} type - 'in' | 'out' | 'deposit'
 * @param {boolean} loading - Show loading state
 */
export function CashMovementDialog({ 
  open, 
  onClose, 
  onConfirm, 
  type = 'in',
  loading = false
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [managerPin, setManagerPin] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankReference, setBankReference] = useState('');
  const [error, setError] = useState('');

  const typeConfig = {
    in: {
      title: 'Cash In',
      icon: ArrowDownToLine,
      description: 'Add cash to the register',
      buttonColor: 'bg-green-600 hover:bg-green-700',
    },
    out: {
      title: 'Cash Out',
      icon: ArrowUpFromLine,
      description: 'Remove cash from the register',
      buttonColor: 'bg-orange-600 hover:bg-orange-700',
    },
    deposit: {
      title: 'Bank Deposit',
      icon: Banknote,
      description: 'Record a bank deposit',
      buttonColor: 'bg-blue-600 hover:bg-blue-700',
    },
  };

  const config = typeConfig[type] || typeConfig.in;
  const Icon = config.icon;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    const amountNum = parseFloat(amount);
    if (!amount || isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    if (!reason.trim()) {
      setError('Please provide a reason');
      return;
    }
    
    if (!managerPin || managerPin.length < 4) {
      setError('Please enter a valid manager PIN');
      return;
    }
    
    onConfirm({
      amount: amountNum,
      reason: reason.trim(),
      managerPin,
      ...(type === 'deposit' && { bankName, bankReference }),
    });
  };

  const handleClose = () => {
    setAmount('');
    setReason('');
    setManagerPin('');
    setBankName('');
    setBankReference('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-background border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Icon className="h-5 w-5 text-teal-500" />
            {config.title}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {config.description}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          
          <div className="space-y-2">
            <Label htmlFor="amount" className="text-muted-foreground">Amount</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₱</span>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-card border-border text-foreground pl-8"
                data-testid="cash-movement-amount"
                autoFocus
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-muted-foreground">Reason</Label>
            <Textarea
              id="reason"
              placeholder={type === 'deposit' ? 'Notes for this deposit' : 'Reason for this cash movement'}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="bg-card border-border text-foreground resize-none"
              rows={2}
              data-testid="cash-movement-reason"
            />
          </div>
          
          {type === 'deposit' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="bankName" className="text-muted-foreground">Bank Name</Label>
                <Input
                  id="bankName"
                  placeholder="e.g., BDO, BPI, Metrobank"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className="bg-card border-border text-foreground"
                  data-testid="deposit-bank-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bankReference" className="text-muted-foreground">Reference Number</Label>
                <Input
                  id="bankReference"
                  placeholder="Deposit slip number"
                  value={bankReference}
                  onChange={(e) => setBankReference(e.target.value)}
                  className="bg-card border-border text-foreground"
                  data-testid="deposit-reference"
                />
              </div>
            </>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="managerPin" className="text-muted-foreground">Manager PIN</Label>
            <Input
              id="managerPin"
              type="password"
              inputMode="numeric"
              maxLength={10}
              placeholder="Enter manager PIN to approve"
              value={managerPin}
              onChange={(e) => setManagerPin(e.target.value.replace(/\D/g, ''))}
              className="bg-card border-border text-foreground"
              data-testid="cash-movement-pin"
            />
          </div>
          
          {error && (
            <p className="text-sm text-red-600" data-testid="cash-movement-error">{error}</p>
          )}
          
          <DialogFooter className="gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              disabled={loading}
              className="border-border text-muted-foreground"
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={loading}
              className={config.buttonColor}
              data-testid="confirm-cash-movement-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                `Confirm ${config.title}`
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CashMovementDialog;
